import type { CSSProperties, ReactNode } from 'react'

export interface ButtonProps {
  variant: 'primary' | 'secondary' | 'ghost' | 'danger'
  size: 'sm' | 'md' | 'lg'
  disabled?: boolean
  loading?: boolean
  children: ReactNode
  onClick?: (() => void) | ((e: React.FormEvent) => void) | ((e: React.MouseEvent) => void)
  type?: 'button' | 'submit'
  className?: string
}

const sizeStyles: Record<ButtonProps['size'], CSSProperties> = {
  sm: {
    height: '32px',
    padding: '0 12px',
    fontSize: '14px',
  },
  md: {
    height: '40px',
    padding: '0 16px',
    fontSize: '15px',
  },
  lg: {
    height: '48px',
    padding: '0 24px',
    fontSize: '16px',
  },
}

const variantStyles: Record<ButtonProps['variant'], CSSProperties> = {
  primary: {
    backgroundColor: 'var(--color-accent)',
    color: '#FFFFFF',
    border: 'none',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.2), var(--shadow-sm)',
  },
  secondary: {
    backgroundColor: 'transparent',
    color: 'var(--color-accent)',
    border: '1.5px solid var(--color-accent)',
  },
  ghost: {
    backgroundColor: 'transparent',
    color: 'var(--color-ink-tertiary)',
    border: 'var(--border-light)',
  },
  danger: {
    backgroundColor: 'var(--color-error)',
    color: '#FFFFFF',
    border: 'none',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.2), var(--shadow-sm)',
  },
}

const variantHoverStyles: Record<ButtonProps['variant'], CSSProperties> = {
  primary: {
    backgroundColor: 'var(--color-accent-hover)',
  },
  secondary: {
    backgroundColor: 'var(--color-accent-light)',
  },
  ghost: {
    backgroundColor: 'rgba(44, 36, 22, 0.05)',
  },
  danger: {
    backgroundColor: '#A84D3D',
  },
}

function Spinner() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      style={{
        animation: 'spin 1s linear infinite',
        marginRight: '8px',
      }}
      aria-hidden="true"
    >
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <circle
        cx="8"
        cy="8"
        r="6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="31.4"
        strokeDashoffset="10"
        opacity="0.7"
      />
    </svg>
  )
}

export function Button({
  variant,
  size,
  disabled = false,
  loading = false,
  children,
  onClick,
  type = 'button',
  className,
}: ButtonProps) {
  const isDisabled = disabled || loading

  const baseStyles: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'var(--font-body)',
    fontWeight: 600,
    borderRadius: 'var(--radius-md)',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 0.5 : 1,
    transition: 'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
    outline: 'none',
    ...sizeStyles[size],
    ...variantStyles[variant],
  }

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isDisabled) {
      const hoverStyles = variantHoverStyles[variant]
      Object.entries(hoverStyles).forEach(([key, value]) => {
        ;(e.currentTarget.style as unknown as Record<string, string>)[key] = value as string
      })
    }
  }

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    const defaultStyles = variantStyles[variant]
    Object.entries(defaultStyles).forEach(([key, value]) => {
      ;(e.currentTarget.style as unknown as Record<string, string>)[key] = value as string
    })
  }

  const handleFocus = (e: React.FocusEvent<HTMLButtonElement>) => {
    // Only show focus ring for keyboard navigation (focus-visible behavior)
    if (e.target.matches(':focus-visible')) {
      e.currentTarget.style.boxShadow = 'var(--focus-ring)'
    }
  }

  const handleBlur = (e: React.FocusEvent<HTMLButtonElement>) => {
    e.currentTarget.style.boxShadow = ''
    // Restore variant box-shadow if it has one
    const defaultStyles = variantStyles[variant]
    if (defaultStyles.boxShadow) {
      e.currentTarget.style.boxShadow = defaultStyles.boxShadow as string
    }
  }

  return (
    <button
      type={type}
      disabled={isDisabled}
      onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
      className={className}
      style={baseStyles}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      aria-busy={loading}
    >
      {loading && <Spinner />}
      {children}
    </button>
  )
}
